import { QueryClientConfig } from "@tanstack/react-query";
import { getRandomBetween } from "utils/getRandomBetween";

export type ApiError = {
  statusCode: number;
  service: string;
  correlationId: string;
  error: string;
};

export type PagedResponse<T> = {
  pageNumber: number;
  pageSize: number;
  results: T[];
  totalResults: number;
  _links: {
    current: Link;
    self: Link;
    next?: Link;
  };
};

export type Link = {
  href: string;
};

export const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
};

export const extractFileNameFromResponse = (
  contentDisposition: string | undefined
): string | undefined => {
  const regex = /filename\*?=(?:UTF-8''|")?([^";]+)/;

  if (!contentDisposition) {
    return undefined;
  } else if (contentDisposition.startsWith("attachment")) {
    const match = regex.exec(contentDisposition);
    if (!match) {
      return undefined;
    } else if (match != null && match[1]) {
      return match[1];
    }
  }
};

function splitIntoRandomChunks(text: string, minSize = 5, maxSize = 15) {
  const chunks = [];
  let i = 0;

  while (i < text.length) {
    const chunkSize = getRandomBetween(minSize, maxSize);
    chunks.push(text.slice(i, i + chunkSize));
    i += chunkSize;
  }

  return chunks;
}

export const handleStreamResponse = (
  response: Response,
  onNewChunk: (chunk: string) => void
): Promise<void> => {
  if (!response.body)
    return Promise.reject(new Error("Streaming not supported"));

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let partialChunk = "";
  const typingQueue = Promise.resolve(); // Ensures sequential execution

  const typeEffect = (text: string): Promise<void> => {
    const chunked = splitIntoRandomChunks(text);

    return chunked.reduce((queue, char) => {
      return queue.then(
        () =>
          new Promise((resolve) => {
            setTimeout(() => {
              onNewChunk(char);
              resolve();
            }, getRandomBetween(3, 10)); // Random delay between 3-10ms
          })
      );
    }, typingQueue);
  };

  return new Promise<void>((resolve, reject) => {
    const processStream = async (): Promise<void> => {
      try {
        const { done, value } = await reader.read();
        if (done) {
          if (partialChunk) onNewChunk(partialChunk);
          resolve(); // ✅ Ensures promise completes
          return;
        }

        const chunk = decoder.decode(value, { stream: true });
        partialChunk += chunk;
        const lines = partialChunk.split("\n");
        partialChunk = lines.pop() ?? ""; // Save last incomplete chunk

        for (const line of lines) {
          if (line.trim()) {
            await typeEffect(line + "\n");
          }
        }

        processStream(); // ✅ Ensures it only continues when the previous read is done
      } catch (error) {
        reject(error);
      }
    };

    processStream().catch(reject);
  });
};
